import { ComponentType, type Row as RowProps } from '@web/graphql-components'
import { ComponentWrapper, PageComponentProps } from '../utils'
import { Section } from '../Row/Row'
import { useMemo } from 'react'
import { CensorInput, VirtualGiftSectionConsumer, VirtualGiftSectionProvider } from './VirtualGiftSectionProvider'
import { DEFAULT_DONATION_FORM } from '../default'
import { Box, HStack } from '@web/components/atoms/Layout'
import { PageButton } from '../PageButton'
import { ChevronLeftIcon } from '@web/components/atoms/Icons'
import { DonationFormProvider } from '../DonationForm'
import { v4 } from 'uuid'

const VirtualGiftSection = (
  props: RowProps &
    PageComponentProps &
    ComponentWrapper & {
      children?: React.ReactNode
      isThankYouStep?: boolean
      censorInput?: CensorInput
    },
) => {
  const childComponents = props.childComponents!
  const virtualItemDownloadId = useMemo(() => v4(), [])
  const allComponents = useMemo(() => {
    // subcomponents already initialised
    if (props.allComponents?.get(props.subComponents?.amountSelector)) {
      return props.allComponents
    }

    // we build the subcomponents here as they inherit settings from the section
    // also they aren't really big enough components to warrant their own component settings

    const components = new Map(props.allComponents)
    const amountSelector = {
      id: props.subComponents?.amountSelector ?? '',
      type: ComponentType.VirtualGiftAmountSelector,
      styles: { ...props.styles, width: '100%', id: 'ge-virtual-gift_quantity' },
      mobileStyles: props.mobileStyles,
    }
    const personalisation = {
      id: props.subComponents?.personalisation ?? '',
      type: ComponentType.VirtualGiftPersonalisation,
      styles: { ...props.styles, width: '100%', id: 'ge-virtual-gift_personalisation' },
      mobileStyles: props.mobileStyles,
    }
    const previewImage = {
      id: props.subComponents?.previewImage ?? '',
      type: ComponentType.VirtualGiftPreviewImage,
      styles: { ...props.styles, id: 'ge-virtual-gift_preview-image' },
      mobileStyles: props.mobileStyles,
    }
    const paymentForm = {
      id: props.subComponents?.paymentForm ?? '',
      type: ComponentType.VirtualGiftPaymentForm,
      styles: { ...props.styles, ...DEFAULT_DONATION_FORM.styles, id: 'ge-virtual-gift_payment-form' },
      mobileStyles: { ...props.mobileStyles, ...DEFAULT_DONATION_FORM.mobileStyles },
    }

    const virtualItemDownload = {
      id: virtualItemDownloadId,
      type: ComponentType.VirtualGiftItemDownload,
      styles: { ...props.styles, id: 'ge-virtual-gift_item-download' },
      mobileStyles: props.mobileStyles,
    }

    components.set(amountSelector.id, amountSelector)
    components.set(personalisation.id, personalisation)
    components.set(previewImage.id, previewImage)
    components.set(paymentForm.id, paymentForm)
    components.set(virtualItemDownload.id, virtualItemDownload)

    return components
  }, [])

  const donationFormSettings = props.styles.paymentForm || DEFAULT_DONATION_FORM

  return (
    <DonationFormProvider settings={{ ...donationFormSettings, styles: { ...donationFormSettings.styles, steps: 3 } }}>
      <VirtualGiftSectionProvider settings={props} censorInput={props.censorInput}>
        <VirtualGiftSectionConsumer>
          {(store) => {
            const step = store?.step || 0

            return (
              <Box display="contents">
                {step > 0 && (
                  <HStack
                    w="100%"
                    justifyContent="space-between"
                    css={{
                      padding: props?.mobileStyles?.padding,
                      py: 0,
                      '@media screen and (min-width: 900px)': {
                        padding: props?.styles?.padding,
                        py: 0,
                      },
                    }}
                  >
                    <PageButton
                      onClick={() => store?.setStep(step - 1)}
                      name="Secondary"
                      size="xs"
                      buttonText="Go Back"
                      iconSlot={<ChevronLeftIcon />}
                    />
                  </HStack>
                )}
                {props.isThankYouStep ? (
                  <Section {...props}>
                    <props.Component
                      {...props}
                      id={virtualItemDownloadId}
                      component={{
                        id: virtualItemDownloadId,
                        type: ComponentType.VirtualGiftItemDownload,
                        styles: props.styles,
                        mobileStyles: props.mobileStyles,
                      }}
                    />
                  </Section>
                ) : (
                  <Section {...props}>
                    {step === 0 && (
                      <>
                        <props.Component {...childComponents[0]} allComponents={allComponents} />
                        <props.Component {...childComponents[1]} allComponents={allComponents} />
                      </>
                    )}
                    {step === 1 && <props.Component {...childComponents[2]} allComponents={allComponents} />}
                    {step === 2 && (
                      <>
                        <props.Component {...childComponents[3]} allComponents={allComponents} />
                        <props.Component {...childComponents[4]} allComponents={allComponents} />
                      </>
                    )}
                  </Section>
                )}
              </Box>
            )
          }}
        </VirtualGiftSectionConsumer>
        {props.children}
      </VirtualGiftSectionProvider>
    </DonationFormProvider>
  )
}

export default VirtualGiftSection
